import { Field, useFormikContext } from 'formik';
import { ChangeEventHandler, useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { FieldProps } from '../../types/fields';
import callApi from '../../utils/callApi';
import { LOADING_TYPES, useCheckoutState } from '../../utils/checkoutState';
import { config, Country } from '../../utils/config';
import debounce from '../../utils/debounce';
import getValidator from '../../utils/getValidator';
import { pushErrorTrackingEventFromErrorObject } from '../../utils/tracking';
import TextField from '../formik/TextField';
import TextFieldWithSuggestions from '../formik/TextFieldWithSuggestions';

type CitySuggestion = {
  name: string;
  nameEn: string;
  postCode: string;
  referenceKey: string;
  regionName: string;
  regionNameEn: string;
};

type StreetSuggestion = {
  name: string;
  nameEn: string;
  referenceKey: string;
};

type AddressFieldBgProps = Omit<FieldProps, 'context'> & {
  context: 'shippingAddress' | 'billingAddress';
};

const AdressFieldBg = (props: AddressFieldBgProps) => {
  const { isLoading, setLoading } = useCheckoutState();
  const { formatMessage } = useIntl();
  const { values, validateField, setFieldValue, setFieldTouched, setFieldError } = useFormikContext<any>();
  const selectedCountry: Country = values[props.context].country;

  const [citySuggestions, setCitySuggestions] = useState<CitySuggestion[]>([]);
  const selectedCity = useRef<CitySuggestion>();

  const [streetSuggestions, setStreetSuggestions] = useState<StreetSuggestion[]>([]);

  const {
    additional: additionalConfig,
    streetHouseNumber: { houseNumber: houseNumberConfig, street: streetConfig },
    zipCodeCity: { zipCode: zipCodeConfig, city: cityConfig }
  } = config.addressForms[selectedCountry];

  const getCitySuggestions = useCallback(
    debounce(async (value: unknown) => {
      try {
        const cityErrors = (await validateField(props.context + '.city')) as any;
        if (cityErrors) {
          return;
        }
        setLoading(LOADING_TYPES.citySuggestionsGet);
        const _citySuggestions = await callApi<CitySuggestion[]>({
          endpoint: '/address/suggest/city',
          urlParams: { searchTerm: value as string },
          flow: 'legacy'
        });

        setCitySuggestions(_citySuggestions);
        if (!_citySuggestions.length) {
          setFieldError(`${props.context}.city`, 'noResults');
        }
      } catch (e) {
        console.error(e);
        pushErrorTrackingEventFromErrorObject(e, 'address_form_bg_get_city_suggestions');
        setFieldError(`${props.context}.city`, 'default');
      }
      setLoading(LOADING_TYPES.off);
    }, 500),
    []
  );

  const onCityChange: ChangeEventHandler<HTMLInputElement> = e => {
    setFieldError(`${props.context}.city`, undefined);
    const trimmedValue = e.currentTarget.value.trim();
    if (values[props.context].city === trimmedValue) {
      return;
    }
    getCitySuggestions(trimmedValue);
  };

  const onCitySelect = (value: CitySuggestion) => {
    selectedCity.current = value;
    setFieldValue(`${props.context}.city`, value.name);
    setTimeout(() => setFieldTouched(`${props.context}.city`), 0);
    setFieldTouched(`${props.context}.zipCode`);
    setFieldValue(`${props.context}.zipCode`, value.postCode);
    setStreetSuggestions([]);
  };

  const getStreetSuggestions = useCallback(
    debounce(async (value: unknown) => {
      try {
        const streetErrors = (await validateField(props.context + '.street')) as any;
        if (streetErrors || !selectedCity.current?.referenceKey) {
          return;
        }
        setLoading(LOADING_TYPES.streetSuggestionsGet);
        const _streetSuggestions = await callApi<StreetSuggestion[]>({
          endpoint: '/address/suggest/street',
          urlParams: { searchTerm: value as string, cityRef: selectedCity.current.referenceKey },
          flow: 'legacy'
        });

        setStreetSuggestions(_streetSuggestions);
        if (!_streetSuggestions.length) {
          setFieldError(`${props.context}.street`, 'noResults');
        }
      } catch (e) {
        console.error(e);
        pushErrorTrackingEventFromErrorObject(e, 'address_form_bg_get_street_suggestions');
        setFieldError(`${props.context}.street`, 'default');
      }
      setLoading(LOADING_TYPES.off);
    }, 500),
    []
  );

  const onStreetChange: ChangeEventHandler<HTMLInputElement> = e => {
    const trimmedStreet = e.currentTarget.value.trim();
    if (values[props.context].street === trimmedStreet) {
      return;
    }
    getStreetSuggestions(trimmedStreet);
  };

  const onStreetSelect = (value: StreetSuggestion) => {
    setFieldValue(`${props.context}.street`, value.name);
  };

  return (
    <>
      <div className="form__row mobile-break">
        <div className="form__field-container" style={{ flex: 2 }}>
          <Field
            name={`${props.context}.city`}
            component={TextFieldWithSuggestions}
            onChange={onCityChange}
            suggestions={citySuggestions.map(city => ({
              value: city,
              label: `${city.name} - ${city.regionName} - ${city.postCode}`
            }))}
            onSelect={onCitySelect}
            label={formatMessage({ id: 'address.city' })}
            maxLength={cityConfig?.maxLength ?? 50}
            validate={getValidator({ required: true })}
            isLoading={isLoading === LOADING_TYPES.citySuggestionsGet}
            data-test-id={`${props.context}-city`}
          />
        </div>
        <div className="form__field-container">
          <Field
            name={`${props.context}.zipCode`}
            component={TextField}
            label={formatMessage({ id: 'address.zipCode' })}
            maxLength={zipCodeConfig.maxLength ?? 8}
            validate={getValidator({ required: true })}
            data-test-id={`${props.context}-zipCode`}
            disabled
          />
        </div>
      </div>
      <div className="form__row mobile-break">
        <div className="form__field-container" style={{ flex: 3 }}>
          <Field
            name={`${props.context}.street`}
            component={TextFieldWithSuggestions}
            suggestions={streetSuggestions.map(street => ({ value: street, label: street.name }))}
            onChange={onStreetChange}
            onSelect={onStreetSelect}
            label={formatMessage({ id: 'address.street' })}
            maxLength={streetConfig?.maxLength ?? 50}
            validate={getValidator({ required: true })}
            isLoading={isLoading === LOADING_TYPES.streetSuggestionsGet}
            data-test-id={`${props.context}-street`}
          />
        </div>
        <div className="form__field-container">
          <Field
            name={`${props.context}.houseNumber`}
            component={TextField}
            label={formatMessage({ id: 'address.houseNumber' })}
            maxLength={houseNumberConfig.maxLength ?? 10}
            validate={getValidator({
              required: true,
              minLength: 1,
              format: /^\d+[-/]?(\d+)?([a-z]+)?/i,
              ...houseNumberConfig
            })}
            data-test-id={`${props.context}-houseNumber`}
          />
        </div>
      </div>
      <div className="form__row">
        <div className="form__field-container">
          <Field
            name={`${props.context}.additional`}
            component={TextField}
            label={`${formatMessage({ id: 'address.additional' })} (${formatMessage({ id: 'generic.optional' })})`}
            maxLength={additionalConfig.maxLength ?? 60}
            validate={getValidator({ format: 'default', ...additionalConfig })}
            data-test-id={`${props.context}-additional`}
          />
        </div>
      </div>
    </>
  );
};

export default AdressFieldBg;
